import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { shopifyApi, formatPrice } from "@/data/api";
import { Loader } from "@/components/ui";

export function ShopRitzyRa() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [products, setProducts] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [affiliateUrl, setAffiliateUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    shopifyApi
      .products(20)
      .then((r) => {
        setConfigured(r.configured);
        setProducts(r.items ?? []);
        setAffiliateUrl(r.affiliate_url ?? "");
      })
      .catch(() => {
        setConfigured(false);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const openAffiliate = () => {
    if (affiliateUrl) window.open(affiliateUrl, "_blank");
    else if (user?.is_admin) navigate("/admin/shopify");
    else toast.show("Your affiliate dashboard link is coming soon 🖤");
  };

  const box = { color: colors.onSurface, fontFamily: fonts.text };

  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      <div style={{ ...box, fontSize: 18, fontFamily: fonts.display, fontWeight: 700, padding: "0 24px" }}>🛍️ Shop RitzyRa Content</div>
      <div style={{ color: colors.muted, fontSize: 13, lineHeight: "19px", padding: "4px 24px 14px" }}>
        Grab product images from the RitzyRa shop to use in your content.
      </div>

      <div style={{ margin: "0 24px 16px", background: colors.brandTertiary, borderRadius: 16, padding: 16 }}>
        <div style={{ color: colors.onBrandTertiary, fontSize: 15, fontFamily: fonts.display, fontWeight: 700 }}>Sharing a product? 🔗</div>
        <div style={{ color: colors.onBrandTertiary, fontSize: 13, lineHeight: "19px", marginTop: 4 }}>
          Open your UpPromote dashboard to create your personal affiliate link so your sales track properly.
        </div>
        <button
          data-testid="affiliate-dashboard"
          className="pressable"
          onClick={openAffiliate}
          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 12, padding: "11px", marginTop: 12, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: fonts.text }}
        >
          <Icon name="external-link" size={15} color={colors.onBrand} />
          My Affiliate Dashboard
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : !configured ? (
        <div style={{ margin: "0 24px", background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <Icon name="shopping-bag" size={26} color={colors.brandSecondary} />
          <div style={{ color: colors.onSurface, fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>Shop not connected yet</div>
          <div style={{ color: colors.muted, fontSize: 13, textAlign: "center", lineHeight: "19px", fontFamily: fonts.text }}>
            {user?.is_admin ? "Connect the RitzyRa Shopify store to pull live products in here — no rebuild needed." : "Live products will appear here soon 🖤"}
          </div>
          {user?.is_admin ? (
            <button data-testid="connect-shop" className="pressable" onClick={() => navigate("/admin/shopify")} style={{ display: "flex", alignItems: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 12, padding: "10px 16px", marginTop: 6, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: fonts.text }}>
              <Icon name="link" size={15} color={colors.onBrand} />
              Connect Shopify
            </button>
          ) : null}
        </div>
      ) : products.length === 0 ? (
        <div style={{ margin: "0 24px", background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <Icon name="package" size={24} color={colors.muted} />
          <div style={{ color: colors.muted, fontSize: 13, textAlign: "center", lineHeight: "19px", fontFamily: fonts.text }}>No products to show right now. Check back soon ✨</div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, overflowX: "auto", padding: "0 24px 4px" }} className="hide-scroll">
          {products.map((p) => (
            <div key={p.id} data-testid={`shop-product-${p.handle}`} style={{ width: 170, flexShrink: 0, background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 10 }}>
              {p.image ? (
                <img src={p.image} alt={p.title} style={{ width: "100%", height: 130, borderRadius: 12, objectFit: "cover", background: colors.surfaceTertiary }} />
              ) : (
                <div style={{ width: "100%", height: 130, borderRadius: 12, background: colors.brandSecondary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="image" size={26} color={colors.onBrand} />
                </div>
              )}
              <div style={{ color: colors.onSurface, fontSize: 14, fontWeight: 700, marginTop: 8, minHeight: 36, fontFamily: fonts.text }}>{p.title}</div>
              {p.price ? <div style={{ color: colors.brandSecondary, fontSize: 13, fontWeight: 700, marginTop: 2, fontFamily: fonts.text }}>{formatPrice(p)}</div> : null}
              <button data-testid={`view-${p.handle}`} className="pressable" onClick={() => window.open(p.url, "_blank")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.surface, padding: "9px", marginTop: 10, cursor: "pointer", color: colors.brand, fontSize: 13, fontWeight: 700, fontFamily: fonts.text }}>
                <Icon name="eye" size={14} color={colors.brand} />
                View product
              </button>
              <div style={{ color: colors.muted, fontSize: 10, textAlign: "center", marginTop: 5, fontStyle: "italic", fontFamily: fonts.text }}>Not your affiliate link</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
